import React, { memo, useCallback, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Popover,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  ButtonBase,
  Collapse,
  Chip,
} from '@mui/material';
import {
  BusinessOutlined as BusinessIcon,
  FolderOutlined as GroupIcon,
  LocationOnOutlined as LocationIcon,
  Search as SearchIcon,
  Check as CheckIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import type { Nursery } from '../types/auth';
import { switchNurseryInUrl } from '../utils/navigation';

interface GroupedNurseries {
  groups: {
    id: number;
    name: string;
    locations: {
      id: number;
      name: string;
      nurseries: Nursery[];
    }[];
  }[];
  standalone: Nursery[];
}

const NurserySelector: React.FC = memo(() => {
  const { nurseries, currentNursery, setCurrentNursery, nurseriesLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('common', { keyPrefix: 'nurserySelector' });
  const isRtl = i18n.language === 'ar';

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [expandedLocations, setExpandedLocations] = useState<Set<number>>(new Set());

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    // Auto-expand the group/location containing the current nursery
    if (currentNursery?.nursery_location) {
      const loc = currentNursery.nursery_location;
      if (loc.id) setExpandedLocations(new Set([loc.id]));
      if (loc.nursery_group?.id) setExpandedGroups(new Set([loc.nursery_group.id]));
    }
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearchTerm('');
  }, []);

  const toggleGroup = useCallback((groupId: number) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const toggleLocation = useCallback((locationId: number) => {
    setExpandedLocations(prev => {
      const next = new Set(prev);
      if (next.has(locationId)) next.delete(locationId);
      else next.add(locationId);
      return next;
    });
  }, []);

  const handleNurserySelect = useCallback((selectedNursery: Nursery) => {
    setCurrentNursery(selectedNursery);
    
    // Navigate to the same page but with the new nursery ID
    const newUrl = switchNurseryInUrl(location.pathname, selectedNursery.nursery_id);
    navigate(newUrl, { replace: true });
    handleClose();
  }, [setCurrentNursery, navigate, location.pathname, handleClose]);

  const filteredNurseries = useMemo(() => {
    if (!searchTerm) return nurseries;
    const term = searchTerm.toLowerCase();
    return nurseries.filter(c => 
      c.name.toLowerCase().includes(term) ||
      (c.nursery_location?.name.toLowerCase().includes(term) ?? false) ||
      (c.nursery_location?.nursery_group?.name.toLowerCase().includes(term) ?? false)
    );
  }, [nurseries, searchTerm]);

  const treeData = useMemo(() => {
    const data: GroupedNurseries = {
      groups: [],
      standalone: []
    };

    const groupMap = new Map<number, typeof data.groups[0]>();
    const locationMap = new Map<number, typeof data.groups[0]['locations'][0]>();

    filteredNurseries.forEach(nursery => {
      const location = nursery.nursery_location;
      const group = location?.nursery_group;

      if (!location || !group) {
        data.standalone.push(nursery);
        return;
      }

      let groupObj = groupMap.get(group.id);
      if (!groupObj) {
        groupObj = { id: group.id, name: group.name, locations: [] };
        groupMap.set(group.id, groupObj);
        data.groups.push(groupObj);
      }

      let locationObj = locationMap.get(location.id);
      if (!locationObj) {
        locationObj = { id: location.id, name: location.name, nurseries: [] };
        locationMap.set(location.id, locationObj);
        groupObj.locations.push(locationObj);
      }

      locationObj.nurseries.push(nursery);
    });

    data.groups.sort((a, b) => a.name.localeCompare(b.name));
    data.groups.forEach(g => {
      g.locations.sort((a, b) => a.name.localeCompare(b.name));
      g.locations.forEach(l => {
        l.nurseries.sort((a, b) => a.name.localeCompare(b.name));
      });
    });
    data.standalone.sort((a, b) => a.name.localeCompare(b.name));

    return data;
  }, [filteredNurseries]);

  const isSearching = searchTerm.length > 0;
  const isGroupExpanded = useCallback((groupId: number) => isSearching || expandedGroups.has(groupId), [isSearching, expandedGroups]);
  const isLocationExpanded = useCallback((locationId: number) => isSearching || expandedLocations.has(locationId), [isSearching, expandedLocations]);

  if (nurseriesLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        py: 2,
        color: 'rgba(255, 255, 255, 0.7)'
      }}>
        <CircularProgress size={16} sx={{ color: 'inherit' }} />
        <Typography variant="caption" sx={{ marginInlineStart: 1 }}>
          {t('loadingNurseries')}
        </Typography>
      </Box>
    );
  }

  if (nurseries.length === 0) {
    return (
      <Box sx={{ 
        py: 1, 
        px: 2, 
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 1,
        color: 'rgba(255, 255, 255, 0.7)'
      }}>
        <Typography variant="caption" textAlign="center">
          {t('noNurseriesAvailable')}
        </Typography>
      </Box>
    );
  }

  if (nurseries.length === 1) {
    const singleNursery = nurseries[0];
    return (
      <Box sx={{ 
        py: 1, 
        px: 2, 
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <BusinessIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.7)' }} />
        <Box>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {t('nursery')}
          </Typography>
          <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
            {singleNursery.name || singleNursery.nursery_id}
          </Typography>
        </Box>
      </Box>
    );
  }

  const open = Boolean(anchorEl);
  const id = open ? 'nursery-selector-popover' : undefined;

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5, display: 'block' }}>
        {t('selectNursery')}
      </Typography>
      
      <ButtonBase
        aria-describedby={id}
        onClick={handleOpen}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          color: 'white',
          borderRadius: 1,
          p: 1.5,
          textAlign: isRtl ? 'right' : 'left',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
          },
          transition: 'background-color 0.2s',
        }}
      >
        <Box sx={{ overflow: 'hidden', flex: 1 }}>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {currentNursery?.name || currentNursery?.nursery_id || ''}
          </Typography>
          {currentNursery?.nursery_location?.nursery_group && (
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }} noWrap>
              {currentNursery.nursery_location.nursery_group.name} &gt; {currentNursery.nursery_location.name}
            </Typography>
          )}
        </Box>
        <ArrowDownIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)', marginInlineStart: 1, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </ButtonBase>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isRtl ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isRtl ? 'right' : 'left',
        }}
        PaperProps={{
          sx: {
            width: anchorEl?.offsetWidth || 280,
            maxWidth: '100vw',
            maxHeight: 450,
            mt: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(15,23,42,0.08)',
            bgcolor: 'background.paper',
          }
        }}
      >
        <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            bgcolor: 'action.hover', 
            borderRadius: 1.5,
            px: 1.5,
            py: 0.5
          }}>
            <SearchIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
            <InputBase
              autoFocus
              fullWidth
              placeholder={t('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                marginInlineStart: 1, 
                fontSize: '0.875rem',
                '& input': {
                  padding: '8px 0',
                }
              }}
            />
          </Box>
        </Box>

        <Box sx={{ overflowY: 'auto', flex: 1, py: 0.5 }}>
          <List sx={{ p: 0 }}>
            {treeData.groups.map(group => {
              const groupOpen = isGroupExpanded(group.id);
              const groupHasSelected = group.locations.some(l =>
                l.nurseries.some(c => c.nursery_id === currentNursery?.nursery_id)
              );
              return (
                <Box key={group.id} sx={{ mb: 0.5 }}>
                  {/* Group header */}
                  <ListItemButton
                    onClick={() => toggleGroup(group.id)}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1,
                      mx: 0.75,
                      gap: 0.5,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 22,
                      height: 22,
                      borderRadius: '4px',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      flexShrink: 0,
                    }}>
                      <GroupIcon sx={{ fontSize: 14 }} />
                    </Box>
                    <ListItemText
                      primary={group.name}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: 600,
                        color: 'text.primary',
                        noWrap: true,
                      }}
                    />
                    {groupHasSelected && !groupOpen && (
                      <Chip size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }} label="●" />
                    )}
                    {groupOpen
                      ? <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      : <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary', transform: isRtl ? 'rotate(180deg)' : 'none' }} />
                    }
                  </ListItemButton>

                  {/* Locations within group */}
                  <Collapse in={groupOpen} timeout="auto" unmountOnExit>
                    <Box sx={{
                      marginInlineStart: 2.25,
                      borderInlineStart: '2px solid',
                      borderColor: 'primary.light',
                      ml: isRtl ? 0 : 2.25,
                      mr: isRtl ? 2.25 : 0,
                    }}>
                      {group.locations.map(loc => {
                        const locOpen = isLocationExpanded(loc.id);
                        const locHasSelected = loc.nurseries.some(c => c.nursery_id === currentNursery?.nursery_id);
                        return (
                          <Box key={loc.id}>
                            {/* Location header */}
                            <ListItemButton
                              onClick={() => toggleLocation(loc.id)}
                              sx={{
                                py: 0.5,
                                px: 1.5,
                                borderRadius: 1,
                                mx: 0.75,
                                gap: 0.5,
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                            >
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                bgcolor: 'grey.100',
                                border: '1.5px solid',
                                borderColor: 'grey.300',
                                flexShrink: 0,
                              }}>
                                <LocationIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                              </Box>
                              <ListItemText
                                primary={loc.name}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  fontWeight: 500,
                                  color: 'text.secondary',
                                  noWrap: true,
                                  fontSize: '0.8125rem',
                                }}
                              />
                              {locHasSelected && !locOpen && (
                                <Chip size="small" color="primary" variant="outlined" sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }} label="●" />
                              )}
                              {locOpen
                                ? <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                : <ChevronRightIcon sx={{ fontSize: 16, color: 'text.disabled', transform: isRtl ? 'rotate(180deg)' : 'none' }} />
                              }
                            </ListItemButton>

                            {/* Nurseries within location */}
                            <Collapse in={locOpen} timeout="auto" unmountOnExit>
                              <Box sx={{
                                marginInlineStart: 2,
                                borderInlineStart: '1.5px dashed',
                                borderColor: 'grey.300',
                                ml: isRtl ? 0 : 2,
                                mr: isRtl ? 2 : 0,
                                pb: 0.5,
                              }}>
                                {loc.nurseries.map(nursery => {
                                  const isSelected = currentNursery?.nursery_id === nursery.nursery_id;
                                  return (
                                    <ListItemButton
                                      key={nursery.nursery_id}
                                      onClick={() => handleNurserySelect(nursery)}
                                      selected={isSelected}
                                      sx={{
                                        py: 0.75,
                                        px: 1.5,
                                        mx: 0.75,
                                        borderRadius: 1,
                                        transition: 'all 0.15s ease',
                                        '&.Mui-selected': {
                                          bgcolor: 'primary.main',
                                          color: 'primary.contrastText',
                                          '&:hover': { bgcolor: 'primary.dark' },
                                          '& .MuiListItemIcon-root, & .MuiTypography-root': { color: 'inherit' },
                                          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                                        },
                                        '&:hover:not(.Mui-selected)': {
                                          bgcolor: 'action.hover',
                                        },
                                      }}
                                    >
                                      <ListItemIcon sx={{ minWidth: 28 }}>
                                        <BusinessIcon sx={{ fontSize: 16 }} />
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={nursery.name}
                                        primaryTypographyProps={{ variant: 'body2', fontWeight: isSelected ? 600 : 400 }}
                                      />
                                      {isSelected && (
                                        <CheckIcon sx={{ fontSize: 16, marginInlineStart: 1 }} />
                                      )}
                                    </ListItemButton>
                                  );
                                })}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}

            {treeData.standalone.length > 0 && (
              <>
                {(treeData.groups.length > 0) && <Divider sx={{ my: 1, mx: 1.5 }} />}
                <Box sx={{ px: 1.5, py: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', px: 0.75 }}>
                    {t('standalone')}
                  </Typography>
                </Box>
                {treeData.standalone.map(nursery => {
                  const isSelected = currentNursery?.nursery_id === nursery.nursery_id;
                  return (
                    <ListItemButton
                      key={nursery.nursery_id}
                      onClick={() => handleNurserySelect(nursery)}
                      selected={isSelected}
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        mx: 0.75,
                        borderRadius: 1,
                        transition: 'all 0.15s ease',
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '& .MuiListItemIcon-root, & .MuiTypography-root': { color: 'inherit' },
                          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                        },
                        '&:hover:not(.Mui-selected)': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <BusinessIcon sx={{ fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={nursery.name}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: isSelected ? 600 : 400 }}
                      />
                      {isSelected && (
                        <CheckIcon sx={{ fontSize: 16, marginInlineStart: 1 }} />
                      )}
                    </ListItemButton>
                  );
                })}
              </>
            )}

            {treeData.groups.length === 0 && treeData.standalone.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('noResults')}
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </Popover>
    </Box>
  );
});

NurserySelector.displayName = 'NurserySelector';

export default NurserySelector;
